import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormApi } from 'final-form';
import validations from 'validations';
import { Button } from 'ncoded-component-library';
import { Form, Field } from 'react-final-form';
import SideDrawer from '../../../SideDrawer';
import InputField from 'components/InputField';
import ChooseAFileInDr from '../../../ChooseAFileInDr';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import { Link, useNavigate } from 'react-router-dom';
import SelectField from 'components/SelectField';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import { TeamInfoField } from '../ManageQuestion/components';
import classNames from 'classnames';
import LinkToIcon from 'icons/LinkTo.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  doesObjectHasTrutfullyValue,
  getCheckedValuesInObject,
  getDataRoomRoute,
  getJoinedWordsWithComma,
  getUserFullNameOrEmail,
} from 'utils';
import { useTranslation } from 'react-i18next';
import { getQASellSideParams } from '../../../QAndARoleFilter/data';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import useAskQuestionDrawer from 'router/subrouters/DataRoom/hooks/useAskQuestionDrawerCommon';
import QuestionRichTextEditor from '../../../QuestionRichTextEditor';
import AnswerRichTextEditor from '../../../AnswerRichTextEditor';
import useCallbackRef from 'hooks/useCallbackRef';
import arrayMutators from 'final-form-arrays';
import AddQuestionToFaq from '../../../AddQuestionToFaq';
import { processFAQAccess } from '../../../FAQAccess/utils';
import { DATA_ROOM_FAQ_ACCESS } from 'router/subrouters/DataRoom/models/FAQ';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import { QuestionSubmissionInitiatedEventProps } from 'models/MixpanelEvents';
import { richTextJsonToPlainText } from 'components/RichTextEditorField/utils';

import './AskQuestionDrawer.styles.scss';

type AskQuestionDrawerProps = {
  onClose?: () => void;
  onSubmitSuccess?: () => void;
};
const QUESTION_TYPE = {
  ASSIGN_Q: 'assign_question',
  ANSWER_D: 'answer_directly',
};

const AskQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  AskQuestionDrawerProps
> = ({ onClose, onSubmitSuccess }, ref) => {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const chooseAFileInDrRef = useRef(null);

  const [nodeToRelateTo, setNodeToRelateTo] = useState<DataRoomNode>(null);
  const [isIndividual, setIsIndividual] = useState(true);
  const [isAnsweringDirectly, setIsAnsweringDirectly] = useState(false);

  const {
    isAdmin,
    isOwner,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { buySideGroups } = useContext(MembersContext);
  const { questionAnswererMembers, faqAccessData } = useContext(QAndAContext);
  const { popServerError, popError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const { questionSubjectsOptions, successNotification } =
    useAskQuestionDrawer();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const onQuestionSubmit = useCallback(
    async (values: any, formApi: FormApi<any, Partial<any>>) => {
      const {
        individuals,
        teams,
        faqAccess,
        faqAccessStages,
        accessDataRoomBuySideMemberIds,
        ...restVals
      } = values;

      const individualAssign = getCheckedValuesInObject(values.individuals);
      const teamAssign = getCheckedValuesInObject(values.teams);

      const teamsAssignedIndividuals = teamAssign.reduce((acc, curr) => {
        const team = Object.entries(
          questionAnswererMembers?.dataRoomQuestionAnswerersGroupByTeams || {},
        )
          .find(([key]) => key === curr)?.[1]
          .map((value) => value.userId);

        if (team) return [...acc, ...team];
        return [...acc];
      }, []);

      const assignedUserIds = isIndividual
        ? individualAssign
        : [...new Set(teamsAssignedIndividuals)];

      const newFaqValues = processFAQAccess(
        { faqAccess, faqAccessStages, accessDataRoomBuySideMemberIds },
        faqAccessData,
      );

      try {
        mixpanelService.questionSubmissionInitiated(
          getMixpanelProps({
            questionContent: richTextJsonToPlainText(values.content),
            questionPriority: values.priority,
            addedToFAQ: values.publishToFAQ,
            relatedDocument: nodeToRelateTo?._id,
            questionSubject:
              values.subject === 'Other' ? values.otherSubject : values.subject,
            buysideBehalf: buySideGroups?.find(
              ({ _id }) => _id === values.dataRoomBuySideGroupId,
            )?.name,
            assignee: getJoinedWordsWithComma(assignedUserIds),
            isOwner,
            userSide: Q_AND_A_SIDES.SELL,
          }) as QuestionSubmissionInitiatedEventProps,
        );

        const { data: question } =
          await dataRoomApi.questionsAndAnswers.askAQuestionSell(dataRoomId, {
            ...restVals,
            assignedUserIds,
            poseQuestionType:
              QUESTION_TYPE[isAnsweringDirectly ? 'ANSWER_D' : 'ASSIGN_Q'],
            ...(nodeToRelateTo && { dataRoomNodeId: nodeToRelateTo._id }),
            ...newFaqValues,
          });

        onSubmitSuccess?.();
        onClose?.();
        successNotification();
        formApi.restart();

        navigate(
          `${getDataRoomRoute(
            dataRoomId,
            'questions/questions-and-answers',
          )}?$or=${getQASellSideParams(question?.status)}`,
        );
      } catch (e) {
        popServerError(e, t('qaError'));
      }
    },
    [
      isIndividual,
      faqAccessData,
      questionAnswererMembers,
      nodeToRelateTo,
      dataRoomId,
      isAnsweringDirectly,
      buySideGroups,
      isOwner,
      getMixpanelProps,
      onSubmitSuccess,
      onClose,
      successNotification,
      navigate,
      popServerError,
      t,
    ],
  );

  const individualCheckbox = useMemo(
    () =>
      questionAnswererMembers?.allDataRoomQuestionAnswerers?.length ? (
        <CheckboxFieldGroup
          className="qa-manage-question__individuals"
          options={questionAnswererMembers.allDataRoomQuestionAnswerers.map(
            (member) => ({
              value: member.userId,
              label: getUserFullNameOrEmail(member),
            }),
          )}
          name="individuals"
          direction="column"
        />
      ) : (
        t('manageQuestionSellOptions.noIndividuals')
      ),
    [questionAnswererMembers, t],
  );

  const teamSelect = useMemo(
    () =>
      Object.keys(
        questionAnswererMembers?.dataRoomQuestionAnswerersGroupByTeams || {},
      ).length
        ? Object.entries(
            questionAnswererMembers.dataRoomQuestionAnswerersGroupByTeams,
          )
            .filter(([_, value]) => !!value.length)
            .map(([key, value]) => (
              <Field
                key={key}
                name={`teams.${key}`}
                type="checkbox"
                teamName={key}
                members={value.map(getUserFullNameOrEmail)}
                component={TeamInfoField}
              />
            ))
        : t('manageQuestionSellOptions.noTeams'),
    [questionAnswererMembers, t],
  );

  return (
    <SideDrawer
      className="qa-ask-question-drawer"
      ref={drawerRef}
      closeAction={() => {
        onClose?.();
        chooseAFileInDrRef.current?.setIsEditing(false);
      }}
    >
      <p className="drawer-title">{t('askAQuestion')}</p>

      {drawer?.open && (
        <Form
          onSubmit={onQuestionSubmit}
          mutators={{ ...arrayMutators }}
          initialValues={{
            faqAccess: DATA_ROOM_FAQ_ACCESS.ALL,
          }}
          render={({
            handleSubmit,
            submitting,
            invalid,
            pristine,
            values: { individuals, teams, subject },
            form: { submit },
          }) => {
            const assigned =
              isAnsweringDirectly ||
              (!isAnsweringDirectly &&
                ((isIndividual && doesObjectHasTrutfullyValue(individuals)) ||
                  (!isIndividual && doesObjectHasTrutfullyValue(teams))));

            return (
              <form onSubmit={handleSubmit}>
                <Field
                  name="subject"
                  label={t('questionSubject')}
                  component={SelectField}
                  options={questionSubjectsOptions}
                  searchable
                  required
                  key={subject}
                  validate={validations.required()}
                />
                {subject === 'Other' && (
                  <Field
                    name="otherSubject"
                    label={t('otherSubject')}
                    component={InputField}
                    maxLength={100}
                    validate={validations.required()}
                    required
                  />
                )}

                <QuestionRichTextEditor label={t('writeBriefQuestion')} />

                <ChooseAFileInDr
                  className="qa-ask-question-drawer__file"
                  dataRoomId={dataRoomId}
                  currenntlySelectedFile={nodeToRelateTo}
                  onChange={setNodeToRelateTo}
                  ref={chooseAFileInDrRef}
                />

                {!!buySideGroups.length && (
                  <>
                    <p className="form-subtitle">{t('askThisQOnbehalf')}</p>
                    <p>{t('chooseWhoRecievesThis')}</p>
                    <Field
                      label={t('selectBuySideGroup')}
                      name="dataRoomBuySideGroupId"
                      options={buySideGroups.map(({ _id, name }) => ({
                        value: _id,
                        label: name,
                      }))}
                      component={SelectField}
                      required
                      validate={validations.required()}
                    />
                  </>
                )}

                <div className="qa-manage-question__assign__select">
                  <Button
                    className={classNames('toggle-opt', {
                      selected: !isAnsweringDirectly,
                    })}
                    onClick={() => setIsAnsweringDirectly(false)}
                  >
                    {t('assignQuestion')}
                  </Button>
                  <Button
                    className={classNames('toggle-opt', {
                      selected: isAnsweringDirectly,
                    })}
                    onClick={() => setIsAnsweringDirectly(true)}
                  >
                    {t('answerDirectly')}
                  </Button>
                </div>
                {isAnsweringDirectly ? (
                  <>
                    <AnswerRichTextEditor
                      label={t('provideAnswer')}
                      filesName="answerUploadedFiles"
                    />
                    <AddQuestionToFaq />
                  </>
                ) : (
                  <>
                    <p>{t('assignQuestionTo')}</p>
                    <div className="qa-manage-question__assign">
                      <div className="qa-manage-question__assign__select">
                        <Button
                          className={classNames('toggle-opt', {
                            selected: isIndividual,
                          })}
                          onClick={() => setIsIndividual(true)}
                        >
                          {t('individual')}
                        </Button>
                        <Button
                          className={classNames('toggle-opt', {
                            selected: !isIndividual,
                          })}
                          onClick={() => setIsIndividual(false)}
                        >
                          {t('team')}
                        </Button>
                      </div>

                      {isAdmin && (
                        <Link
                          to={getDataRoomRoute(dataRoomId, 'members')}
                          className="qa-manage-question__assign__members"
                        >
                          <LinkToIcon /> {t('manageTeams')}
                        </Link>
                      )}
                    </div>

                    {isIndividual ? individualCheckbox : teamSelect}
                  </>
                )}
                <div className="qa-manage-question-buy__form__actions">
                  <Button variant="link" onClick={onClose}>
                    {t('cancel')}
                  </Button>
                  <Button
                    disabled={submitting || invalid || pristine}
                    onClick={() => {
                      if (assigned) {
                        submit();
                      } else popError(t('assignQuestionToSomeone'));
                    }}
                  >
                    {t('submitYourOwnQuestion')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(AskQuestionDrawer);
